/**
 * BrownWireReport - The Wire Report for Brown Bears Basketball
 * Single-video analysis page with frame-by-frame controls, markers, chat, and Brown-specific data
 *
 * UX Flow: Video-First → User Triggers Analysis → Matrix Animation (15s) → Report Reveal
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Share2,
  List,
  Calendar,
  Trophy,
  Sparkles,
  Zap,
  Users,
  Target,
  Shield,
} from 'lucide-react';

// Import shared components
import BasketballVideoPlayer from './BasketballVideoPlayer';
import MarkerOverlay from './MarkerOverlay';
import MatrixBackground from './MatrixBackground';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../ui/resizable';

// Import Brown-specific components
import BrownVideoChat from './BrownVideoChat';

// Import Brown Bears data
import {
  gameMetadata,
  keyMoments,
  playerAnalysis,
  tacticalBreakdown,
  benchDynamics,
  recommendations,
  ivyLeagueContext,
  translations as allTranslations,
  patternPredictions,
  harvardDefensiveScouting,
} from '../../data/basketball/brownBearsData';

// Analysis state enum
const ANALYSIS_STATES = {
  VIDEO_READY: 'video-ready',
  ANALYZING: 'analyzing',
  COMPLETE: 'complete',
};

// Analyze Button Component (Brown themed)
const AnalyzeButton = ({ onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="group relative w-full max-w-md mx-auto flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-700 to-amber-800 text-white font-bold text-lg rounded-xl shadow-lg shadow-amber-700/25 hover:shadow-amber-700/40 hover:from-amber-600 hover:to-amber-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
  >
    <Sparkles className="w-6 h-6 group-hover:animate-pulse" />
    <span>Analyze with SLAM AI</span>
    <Zap className="w-5 h-5 group-hover:animate-bounce" />
  </button>
);

// Basketball Icon SVG
const BasketballIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="2.5" fill="none" />
    <line x1="4" y1="32" x2="60" y2="32" stroke="currentColor" strokeWidth="2" />
    <line x1="32" y1="4" x2="32" y2="60" stroke="currentColor" strokeWidth="2" />
    <path d="M32 4 Q14 18, 14 32 Q14 46, 32 60" stroke="currentColor" strokeWidth="2" fill="none" />
    <path d="M32 4 Q50 18, 50 32 Q50 46, 32 60" stroke="currentColor" strokeWidth="2" fill="none" />
  </svg>
);

// Analysis Progress Overlay
const AnalysisProgress = ({ progress, onSkip }) => {
  const phases = [
    { text: 'Analyzing Play Patterns...', range: [0, 33] },
    { text: 'Processing Ball Screen Actions...', range: [33, 66] },
    { text: 'Generating Scouting Report...', range: [66, 100] },
  ];

  const currentPhase = phases.find(p => progress >= p.range[0] && progress < p.range[1]) || phases[2];
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div
      className="fixed inset-0 z-[55] flex items-center justify-center cursor-pointer"
      onClick={onSkip}
    >
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/30 text-sm animate-pulse">
        Click anywhere to skip
      </div>

      <div
        className="bg-black/80 backdrop-blur-xl border border-white/[0.12] rounded-2xl p-8 max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative w-44 h-44 mx-auto mb-6">
          <svg className="w-full h-full" viewBox="0 0 176 176">
            <circle
              cx="88"
              cy="88"
              r={radius}
              fill="none"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="6"
            />
            <circle
              cx="88"
              cy="88"
              r={radius}
              fill="none"
              stroke="url(#brownProgressGradient)"
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              style={{
                transform: 'rotate(-90deg)',
                transformOrigin: '88px 88px',
                transition: 'stroke-dashoffset 0.3s ease',
                filter: 'drop-shadow(0 0 8px rgba(180, 83, 9, 0.6))'
              }}
            />
            <defs>
              <linearGradient id="brownProgressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#b45309" />
                <stop offset="100%" stopColor="#78350f" />
              </linearGradient>
            </defs>
          </svg>

          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <BasketballIcon className="w-12 h-12 text-amber-600 mb-1" />
            <span className="text-amber-500 text-2xl font-bold" style={{ textShadow: '0 0 20px rgba(180, 83, 9, 0.5)' }}>
              {Math.round(progress)}%
            </span>
          </div>
        </div>

        <p className="text-white text-center font-medium mb-2">{currentPhase.text}</p>
        <p className="text-white/50 text-center text-sm">SLAM AI is analyzing Brown Bears offense</p>
      </div>
    </div>
  );
};

// Report Reveal Wrapper
const ReportReveal = ({ children, delay = 0, visible }) => (
  <div
    className={`transition-all duration-700 ease-out ${
      visible
        ? 'opacity-100 translate-y-0'
        : 'opacity-0 translate-y-8 pointer-events-none'
    }`}
    style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
  >
    {children}
  </div>
);

// Key Moments Timeline (Brown themed)
const KeyMomentsTimeline = ({ moments, currentMoment, onMomentClick, translations }) => {
  const timelineRef = useRef(null);

  useEffect(() => {
    if (currentMoment && timelineRef.current) {
      const element = timelineRef.current.querySelector(`[data-moment-id="${currentMoment.id}"]`);
      if (element) {
        const container = timelineRef.current;
        const elementLeft = element.offsetLeft;
        const containerWidth = container.clientWidth;
        const elementWidth = element.offsetWidth;
        const scrollTarget = elementLeft - (containerWidth / 2) + (elementWidth / 2);
        container.scrollTo({ left: scrollTarget, behavior: 'smooth' });
      }
    }
  }, [currentMoment?.id]);

  const categoryColors = {
    clutch: 'bg-red-500',
    shooting: 'bg-green-500',
    drive: 'bg-blue-500',
    playmaking: 'bg-purple-500',
    defense: 'bg-orange-500',
    rebounding: 'bg-cyan-500',
  };

  return (
    <div className="glass-panel rounded-xl p-4">
      <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
        <List className="w-4 h-4 text-amber-500" />
        {translations.keyMoments || 'Key Plays'}
        <span className="ml-auto text-white/50 text-sm font-normal">
          {moments.length} plays
        </span>
      </h3>
      <div
        ref={timelineRef}
        className="flex gap-3 overflow-x-auto pb-2 custom-scrollbar"
      >
        {moments.map((moment) => {
          const isActive = currentMoment?.id === moment.id;
          return (
            <button
              key={moment.id}
              data-moment-id={moment.id}
              onClick={() => onMomentClick(moment)}
              className={`flex-shrink-0 w-52 p-3 rounded-lg border transition-all text-left backdrop-blur-sm ${
                isActive
                  ? 'bg-amber-700/20 border-amber-600'
                  : 'bg-white/[0.04] border-white/[0.15] hover:border-white/30 hover:bg-white/[0.08]'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-2 h-2 rounded-full ${categoryColors[moment.category] || 'bg-gray-500'}`} />
                <span className="text-white/60 text-xs font-mono">{moment.timeVideo}</span>
                <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold rounded bg-amber-700/50 text-white">
                  BROWN
                </span>
              </div>
              <h4 className="text-white text-sm font-medium truncate">{moment.event}</h4>
              {moment.player && (
                <p className="text-white/50 text-xs mt-1 truncate">{moment.player}</p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Brown-specific Game Data Panel
const BrownGameDataPanel = ({ playerAnalysis, tacticalBreakdown, recommendations, ivyLeagueContext, patternPredictions, harvardScouting, translations }) => {
  const [activeTab, setActiveTab] = useState('personnel');

  const tabs = [
    { id: 'personnel', label: 'Personnel', icon: Users },
    { id: 'scheme', label: 'Scheme', icon: Target },
    { id: 'defense', label: 'Defense', icon: Shield },
    ...(harvardScouting ? [{ id: 'vsHarvard', label: 'vs Harvard', icon: Zap }] : []),
  ];

  return (
    <div className="space-y-4">
      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-white/[0.15] pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-amber-700/30 text-amber-400'
                : 'text-white/60 hover:text-white hover:bg-white/[0.08]'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Personnel Tab */}
      {activeTab === 'personnel' && (
        <div className="space-y-4">
          {playerAnalysis.map((player, idx) => (
            <div key={idx} className="glass-panel rounded-xl p-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-12 h-12 rounded-lg bg-amber-700/30 flex items-center justify-center text-amber-500 font-bold text-lg">
                  {player.number}
                </div>
                <div className="flex-1">
                  <h4 className="text-white font-semibold">{player.player}</h4>
                  <p className="text-white/50 text-sm">{player.position} | {player.role}</p>
                </div>
              </div>

              {player.stats && Object.keys(player.stats).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {player.stats.ppg && (
                    <span className="px-2 py-1 bg-white/[0.08] rounded text-xs text-white/80">{player.stats.ppg} PPG</span>
                  )}
                  {player.stats.apg && (
                    <span className="px-2 py-1 bg-white/[0.08] rounded text-xs text-white/80">{player.stats.apg} APG</span>
                  )}
                  {player.stats.rpg && (
                    <span className="px-2 py-1 bg-white/[0.08] rounded text-xs text-white/80">{player.stats.rpg} RPG</span>
                  )}
                  {player.stats.fg && (
                    <span className="px-2 py-1 bg-white/[0.08] rounded text-xs text-white/80">{player.stats.fg} FG</span>
                  )}
                  {player.stats.threePt && (
                    <span className="px-2 py-1 bg-green-500/20 rounded text-xs text-green-400">{player.stats.threePt} 3PT</span>
                  )}
                </div>
              )}

              <p className="text-white/70 text-sm mb-2">{player.tacticalImpact}</p>

              {player.scoutingNote && (
                <div className="mt-2 p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-300 text-xs font-medium">{player.scoutingNote}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Scheme Tab */}
      {activeTab === 'scheme' && (
        <div className="space-y-4">
          <div className="glass-panel rounded-xl p-4">
            <h4 className="text-white font-semibold mb-2">Offensive System</h4>
            <p className="text-amber-400 text-sm font-medium mb-2">{tacticalBreakdown.brownOffense.scheme}</p>
            <p className="text-white/70 text-sm mb-3">{tacticalBreakdown.brownOffense.philosophy}</p>

            <h5 className="text-white/80 text-sm font-medium mb-2">Key Actions:</h5>
            <div className="space-y-2">
              {tacticalBreakdown.brownOffense.keyActions.map((action, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-white/[0.04] rounded-lg">
                  <span className="text-white/80 text-sm">{action.action}</span>
                  <div className="flex gap-2">
                    <span className={`px-2 py-0.5 text-xs rounded ${
                      action.frequency === 'HIGH' ? 'bg-red-500/20 text-red-400' :
                      action.frequency === 'MEDIUM' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-green-500/20 text-green-400'
                    }`}>
                      {action.frequency}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-panel rounded-xl p-4">
            <h4 className="text-white font-semibold mb-2">Pattern Predictions</h4>
            <div className="space-y-2">
              {patternPredictions.situationalPredictions.map((pred, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-white/[0.04] rounded-lg">
                  <span className="text-white/60 text-sm">{pred.situation}</span>
                  <span className="text-white/80 text-sm">{pred.action}</span>
                  <span className={`px-2 py-0.5 text-xs rounded ${
                    pred.confidence === 'HIGH' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {pred.confidence}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-panel rounded-xl p-4">
            <h4 className="text-white font-semibold mb-2">Ivy League Context</h4>
            <p className="text-amber-400 text-lg font-bold mb-2">{ivyLeagueContext.standings.record}</p>
            <div className="space-y-1">
              {ivyLeagueContext.recentResults.slice(0, 4).map((result, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <span className="text-white/60">{result.opponent}</span>
                  <span className={result.result.startsWith('W') ? 'text-green-400' : 'text-red-400'}>
                    {result.result}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Defense Tab */}
      {activeTab === 'defense' && (
        <div className="space-y-4">
          <div className="glass-panel rounded-xl p-4">
            <h4 className="text-white font-semibold mb-3">Ball Screen Coverage</h4>

            <h5 className="text-white/80 text-sm font-medium mb-2">By Handler:</h5>
            <div className="space-y-2 mb-4">
              {tacticalBreakdown.ballScreenCoverage.byHandler.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-white/[0.04] rounded-lg">
                  <span className="text-white/80 text-sm">{item.handler}</span>
                  <span className="text-amber-400 text-sm font-medium">{item.coverage}</span>
                </div>
              ))}
            </div>

            <h5 className="text-white/80 text-sm font-medium mb-2">By Screener:</h5>
            <div className="space-y-2">
              {tacticalBreakdown.ballScreenCoverage.byScreener.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-white/[0.04] rounded-lg">
                  <span className="text-white/80 text-sm">{item.screener}</span>
                  <span className="text-amber-400 text-sm font-medium">{item.coverage}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-panel rounded-xl p-4">
            <h4 className="text-white font-semibold mb-3">Defensive Game Plan</h4>
            <div className="space-y-3">
              {recommendations.map((rec, idx) => (
                <div key={idx} className="p-3 bg-white/[0.04] rounded-lg border-l-2 border-amber-600">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                      rec.priority === 'high' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {rec.priority.toUpperCase()}
                    </span>
                    <span className="text-white font-medium text-sm">{rec.focus}</span>
                  </div>
                  <p className="text-white/70 text-sm">{rec.action}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-panel rounded-xl p-4">
            <h4 className="text-white font-semibold mb-3">What They Struggle Against</h4>
            <ul className="space-y-2">
              {benchDynamics.opponent.howToExploit.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <span className="text-green-400">✓</span>
                  <span className="text-white/70">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* vs Harvard Tab */}
      {activeTab === 'vsHarvard' && harvardScouting && (
        <div className="space-y-4">
          {/* Priority Banner */}
          <div className="glass-panel rounded-xl p-4 border border-red-500/30 bg-red-500/5">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs font-bold rounded">URGENT</span>
              <h4 className="text-white font-semibold">vs Harvard Crimson — Game Prep</h4>
            </div>
            <p className="text-white/60 text-sm">Attacking Harvard's switching defense, Diamond zone, and zone OOB packages.</p>
          </div>

          {/* Game Plan Priority */}
          <div className="glass-panel rounded-xl p-4">
            <h4 className="text-white font-semibold mb-3">Game Plan Priority</h4>
            <div className="space-y-2">
              {harvardScouting.gamePlanPriority.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-2.5 bg-white/[0.04] rounded-lg">
                  <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    idx < 2 ? 'bg-red-500/20 text-red-400' : idx < 4 ? 'bg-amber-500/20 text-amber-400' : 'bg-green-500/20 text-green-400'
                  }`}>
                    {item.rank}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white/90 text-sm font-medium">{item.action}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-white/40 text-xs">vs {item.against}</span>
                      <span className="text-amber-400 text-xs">→ {item.result}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Switching Defense */}
          <div className="glass-panel rounded-xl p-4">
            <h4 className="text-white font-semibold mb-1">{harvardScouting.switchingDefense.title}</h4>
            <p className="text-white/50 text-xs mb-3">{harvardScouting.switchingDefense.description}</p>

            <h5 className="text-amber-400 text-sm font-medium mb-2">On-Ball Actions</h5>
            <div className="space-y-2 mb-4">
              {harvardScouting.switchingDefense.onBall.map((item, idx) => (
                <div key={idx} className={`p-2.5 bg-white/[0.04] rounded-lg border-l-2 ${
                  item.priority === 'high' ? 'border-red-500' : 'border-amber-500'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white font-medium text-sm">{item.concept}</span>
                    <span className={`px-1.5 py-0.5 text-[10px] rounded ${
                      item.priority === 'high' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'
                    }`}>{item.priority.toUpperCase()}</span>
                  </div>
                  <p className="text-white/60 text-xs">{item.action}</p>
                  <p className="text-amber-400/70 text-[10px] mt-1">{item.personnel}</p>
                </div>
              ))}
            </div>

            <h5 className="text-amber-400 text-sm font-medium mb-2">Off-Ball Actions</h5>
            <div className="space-y-2">
              {harvardScouting.switchingDefense.offBall.map((item, idx) => (
                <div key={idx} className={`p-2.5 bg-white/[0.04] rounded-lg border-l-2 ${
                  item.priority === 'high' ? 'border-red-500' : 'border-amber-500'
                }`}>
                  <span className="text-white font-medium text-sm">{item.concept}</span>
                  <p className="text-white/60 text-xs mt-1">{item.action}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Diamond Zone */}
          <div className="glass-panel rounded-xl p-4">
            <h4 className="text-white font-semibold mb-1">{harvardScouting.diamondZone.title}</h4>
            <p className="text-white/50 text-xs mb-3">{harvardScouting.diamondZone.description}</p>

            <h5 className="text-red-400 text-sm font-medium mb-2">Zone Vulnerabilities</h5>
            <ul className="space-y-1.5 mb-4">
              {harvardScouting.diamondZone.vulnerabilities.map((vuln, idx) => (
                <li key={idx} className="flex items-start gap-2 text-xs">
                  <span className="text-red-400 mt-0.5">●</span>
                  <span className="text-white/70">{vuln}</span>
                </li>
              ))}
            </ul>

            <h5 className="text-amber-400 text-sm font-medium mb-2">Zone Beater Plays</h5>
            <div className="space-y-2">
              {harvardScouting.diamondZone.zoneBeaterPlays.map((play, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-white/[0.04] rounded-lg">
                  <div>
                    <span className="text-white font-medium text-sm">"{play.name}"</span>
                    <span className="text-white/40 text-xs ml-2">({play.entry})</span>
                  </div>
                  <span className="text-amber-400 text-xs">{play.target}</span>
                </div>
              ))}
            </div>

            <h5 className="text-amber-400 text-sm font-medium mt-4 mb-2">Attacking Concepts</h5>
            <div className="space-y-2">
              {harvardScouting.diamondZone.attackingConcepts.map((concept, idx) => (
                <div key={idx} className={`p-2.5 bg-white/[0.04] rounded-lg border-l-2 ${
                  concept.priority === 'high' ? 'border-red-500' : 'border-amber-500'
                }`}>
                  <span className="text-white font-medium text-sm">{concept.name}</span>
                  <p className="text-white/60 text-xs mt-1">{concept.description}</p>
                  <p className="text-amber-400/70 text-[10px] mt-1 italic">{concept.play}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Zone OOB */}
          <div className="glass-panel rounded-xl p-4">
            <h4 className="text-white font-semibold mb-1">{harvardScouting.zoneOOB.title}</h4>
            <div className="p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-3">
              <p className="text-amber-300 text-xs">{harvardScouting.zoneOOB.keyPrinciple}</p>
            </div>

            <h5 className="text-amber-400 text-sm font-medium mb-2">BLOB Plays</h5>
            <div className="space-y-2 mb-4">
              {harvardScouting.zoneOOB.blob.map((play, idx) => (
                <div key={idx} className={`p-2.5 bg-white/[0.04] rounded-lg border-l-2 ${
                  play.priority === 'high' ? 'border-red-500' : 'border-amber-500'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white font-medium text-sm">"{play.name}"</span>
                    <span className="text-white/40 text-xs">{play.formation}</span>
                  </div>
                  <p className="text-white/60 text-xs">{play.action}</p>
                  <div className="mt-1.5 space-y-0.5">
                    {play.reads.map((read, rIdx) => (
                      <p key={rIdx} className="text-amber-400/70 text-[10px]">→ {read}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <h5 className="text-amber-400 text-sm font-medium mb-2">SLOB Plays</h5>
            <div className="space-y-2">
              {harvardScouting.zoneOOB.slob.map((play, idx) => (
                <div key={idx} className="p-2.5 bg-white/[0.04] rounded-lg border-l-2 border-amber-500">
                  <span className="text-white font-medium text-sm">"{play.name}"</span>
                  <span className="text-white/40 text-xs ml-2">{play.formation}</span>
                  <p className="text-white/60 text-xs mt-1">{play.action}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Additional Ideas */}
          <div className="glass-panel rounded-xl p-4">
            <h4 className="text-white font-semibold mb-3">Additional Offensive Ideas</h4>
            <div className="space-y-2">
              {harvardScouting.additionalIdeas.map((idea, idx) => (
                <div key={idx} className={`p-2.5 bg-white/[0.04] rounded-lg border-l-2 ${
                  idea.priority === 'high' ? 'border-red-500' : 'border-amber-500'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white font-medium text-sm">{idea.concept}</span>
                    <span className={`px-1.5 py-0.5 text-[10px] rounded ${
                      idea.priority === 'high' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'
                    }`}>{idea.priority.toUpperCase()}</span>
                  </div>
                  <p className="text-white/60 text-xs">{idea.description}</p>
                  {idea.reference && (
                    <p className="text-amber-400/50 text-[10px] mt-1 italic">{idea.reference}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Game Header (Brown themed)
const GameHeader = ({ metadata, ivyContext, translations, onBack }) => {
  return (
    <div className="bg-black/95 backdrop-blur-xl border-b border-white/[0.15] sticky top-0 z-30 shrink-0">
      <div className="w-full px-3 py-2">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Back + Badge */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={onBack}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-white" />
            </button>
            <span className="px-2 py-0.5 bg-amber-700 text-white text-[10px] font-bold rounded">
              {translations.title || 'THE WIRE'}
            </span>
          </div>

          {/* Center: Team Info */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-amber-800 flex items-center justify-center">
              <span className="text-white font-bold text-[10px]">BRN</span>
            </div>
            <div className="text-center">
              <h1 className="text-white font-bold text-lg">Brown Bears</h1>
              <p className="text-white/60 text-xs">{ivyContext?.standings?.record || '7-10 (1-3 Ivy)'}</p>
            </div>
          </div>

          {/* Right: Metadata + Actions */}
          <div className="flex items-center gap-2 shrink-0">
            <div className="hidden lg:flex items-center gap-3 text-white/50 text-xs mr-2">
              <span className="flex items-center gap-1">
                <Trophy className="w-3 h-3" />
                {metadata.competition}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {metadata.date}
              </span>
            </div>
            <button className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" title="Share">
              <Share2 className="w-4 h-4 text-white/60" />
            </button>
            <button className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" title="Download">
              <Download className="w-4 h-4 text-white/60" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const BrownWireReport = () => {
  const navigate = useNavigate();
  const [language] = useState('en');
  const [currentTime, setCurrentTime] = useState(0);
  const [currentMarker, setCurrentMarker] = useState(null);
  const [showMarkerOverlay, setShowMarkerOverlay] = useState(false);
  const [chatMinimized, setChatMinimized] = useState(false);

  // Analysis state
  const [analysisState, setAnalysisState] = useState(ANALYSIS_STATES.VIDEO_READY);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const analysisTimerRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const translations = allTranslations[language] || allTranslations.en;

  const isAnalyzing = analysisState === ANALYSIS_STATES.ANALYZING;
  const isReportVisible = analysisState === ANALYSIS_STATES.COMPLETE;

  const handleTimeUpdate = useCallback((time) => {
    setCurrentTime(time);
  }, []);

  const handleMarkerHit = useCallback((marker) => {
    if (analysisState === ANALYSIS_STATES.COMPLETE) {
      setCurrentMarker(marker);
      setShowMarkerOverlay(true);
    }
  }, [analysisState]);

  const handleMarkerClick = useCallback((marker) => {
    setCurrentMarker(marker);
    setShowMarkerOverlay(true);
  }, []);

  const handleOverlayDismiss = useCallback(() => {
    setShowMarkerOverlay(false);
    setCurrentMarker(null);
  }, []);

  const handleOverlayResume = useCallback(() => {
    setShowMarkerOverlay(false);
  }, []);

  const handleStartAnalysis = useCallback(() => {
    setAnalysisState(ANALYSIS_STATES.ANALYZING);
    setAnalysisProgress(0);

    const duration = 15000;
    const startTime = Date.now();

    const updateProgress = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / duration) * 100, 100);
      setAnalysisProgress(progress);

      if (progress < 100) {
        analysisTimerRef.current = requestAnimationFrame(updateProgress);
      } else {
        setAnalysisState(ANALYSIS_STATES.COMPLETE);
      }
    };

    analysisTimerRef.current = requestAnimationFrame(updateProgress);
  }, []);

  const handleSkipAnalysis = useCallback(() => {
    if (analysisTimerRef.current) {
      cancelAnimationFrame(analysisTimerRef.current);
    }
    setAnalysisProgress(100);
    setAnalysisState(ANALYSIS_STATES.COMPLETE);
  }, []);

  useEffect(() => {
    return () => {
      if (analysisTimerRef.current) {
        cancelAnimationFrame(analysisTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (analysisState === ANALYSIS_STATES.COMPLETE) {
      window.scrollTo({ top: 0, behavior: 'instant' });
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
    }
  }, [analysisState]);

  // Video URL - Brown Offensive Breakdowns from GCS
  const videoUrl = "https://storage.googleapis.com/slamsportsai.appspot.com/demos/brown-offensive-breakdowns.mov";
  const posterUrl = null;

  // Build game data object for chat
  const gameDataForChat = {
    gameMetadata,
    keyMoments,
    playerAnalysis,
    tacticalBreakdown,
    ivyLeagueContext,
    patternPredictions,
  };

  return (
    <div className="flex flex-col h-full text-white bg-black">
      {/* Matrix animation */}
      <MatrixBackground isActive={isAnalyzing} phase="analyzing" />

      {/* Analysis Progress */}
      {isAnalyzing && (
        <AnalysisProgress
          progress={analysisProgress}
          onSkip={handleSkipAnalysis}
        />
      )}

      {/* Header */}
      <GameHeader
        metadata={gameMetadata}
        ivyContext={ivyLeagueContext}
        translations={translations}
        onBack={() => navigate(-1)}
      />

      {/* Main Content */}
      <div ref={scrollContainerRef} className="flex-1 overflow-hidden">
        {/* Pre-analysis view */}
        {!isReportVisible && (
          <div className="h-full overflow-y-auto px-4 md:px-6 py-4 md:py-6">
            <div className="max-w-2xl mx-auto space-y-4">
              <div className="relative">
                {videoUrl ? (
                  <BasketballVideoPlayer
                    videoUrl={videoUrl}
                    posterUrl={posterUrl}
                    markers={[]}
                    currentMarker={currentMarker}
                    onTimeUpdate={handleTimeUpdate}
                    onMarkerHit={handleMarkerHit}
                    onMarkerClick={handleMarkerClick}
                    isAnalyzing={isAnalyzing}
                    translations={translations}
                  />
                ) : (
                  <div className="aspect-video bg-gradient-to-br from-amber-900/30 to-amber-950/50 rounded-xl flex flex-col items-center justify-center border border-amber-700/30">
                    <BasketballIcon className="w-20 h-20 text-amber-600 mb-4" />
                    <h3 className="text-white font-bold text-xl mb-2">Brown Bears Offensive Analysis</h3>
                    <p className="text-white/60 text-sm text-center max-w-md">
                      Video footage available in source files. Click below to generate the AI scouting report.
                    </p>
                  </div>
                )}
              </div>
              {analysisState === ANALYSIS_STATES.VIDEO_READY && (
                <div className="py-6">
                  <p className="text-center text-white/60 mb-4">
                    Generate comprehensive offensive scouting report for Brown Bears
                  </p>
                  <AnalyzeButton onClick={handleStartAnalysis} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Post-analysis: Resizable panels */}
        {isReportVisible && (
          <ResizablePanelGroup direction="horizontal" className="h-full">
            {/* Left Panel: Video + Timeline + Chat */}
            <ResizablePanel defaultSize={55} minSize={35} maxSize={75}>
              <div className="h-full overflow-y-auto p-4 space-y-4">
                {/* Video player or placeholder */}
                <div className="relative">
                  {videoUrl ? (
                    <>
                      <BasketballVideoPlayer
                        videoUrl={videoUrl}
                        posterUrl={posterUrl}
                        markers={keyMoments}
                        currentMarker={currentMarker}
                        onTimeUpdate={handleTimeUpdate}
                        onMarkerHit={handleMarkerHit}
                        onMarkerClick={handleMarkerClick}
                        isAnalyzing={isAnalyzing}
                        translations={translations}
                      />
                      {showMarkerOverlay && (
                        <MarkerOverlay
                          marker={currentMarker}
                          onDismiss={handleOverlayDismiss}
                          onResume={handleOverlayResume}
                          position="top-left"
                          autoCollapseAfter={0}
                        />
                      )}
                    </>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-amber-900/30 to-amber-950/50 rounded-xl flex flex-col items-center justify-center border border-amber-700/30">
                      <BasketballIcon className="w-16 h-16 text-amber-600 mb-3" />
                      <p className="text-white/60 text-sm">Video: Brown Offensive Breakdowns.mov</p>
                      <p className="text-white/40 text-xs">937MB | Available in source directory</p>
                    </div>
                  )}
                </div>

                {/* Key moments timeline */}
                <ReportReveal visible={isReportVisible} delay={200}>
                  <KeyMomentsTimeline
                    moments={keyMoments}
                    currentMoment={currentMarker}
                    onMomentClick={handleMarkerClick}
                    translations={translations}
                  />
                </ReportReveal>

                {/* Chat - desktop */}
                <ReportReveal visible={isReportVisible} delay={800}>
                  <div className="hidden lg:block">
                    <BrownVideoChat
                      gameData={gameDataForChat}
                      currentMarker={currentMarker}
                      translations={translations}
                      isMinimized={chatMinimized}
                      onToggleMinimize={() => setChatMinimized(!chatMinimized)}
                    />
                  </div>
                </ReportReveal>
              </div>
            </ResizablePanel>

            {/* Resize Handle */}
            <ResizableHandle className="w-1 bg-white/[0.08] hover:bg-amber-500/30 transition-colors data-[resize-handle-active]:bg-amber-500/50" />

            {/* Right Panel: Game Data */}
            <ResizablePanel defaultSize={45} minSize={25} maxSize={65}>
              <div className="h-full overflow-y-auto p-4 custom-scrollbar">
                <ReportReveal visible={isReportVisible} delay={400}>
                  <BrownGameDataPanel
                    playerAnalysis={playerAnalysis}
                    tacticalBreakdown={tacticalBreakdown}
                    recommendations={recommendations}
                    ivyLeagueContext={ivyLeagueContext}
                    patternPredictions={patternPredictions}
                    harvardScouting={harvardDefensiveScouting}
                    translations={translations}
                  />
                </ReportReveal>
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        )}

        {/* Mobile chat */}
        {isReportVisible && (
          <div className="lg:hidden fixed bottom-0 left-0 right-0 z-20">
            <BrownVideoChat
              gameData={gameDataForChat}
              currentMarker={currentMarker}
              translations={translations}
              isMinimized={chatMinimized}
              onToggleMinimize={() => setChatMinimized(!chatMinimized)}
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-white/[0.15] py-4 mt-auto shrink-0">
        <div className="w-full px-4 flex items-center justify-between text-white/40 text-xs">
          <span>SLAM Sports AI · Brown Bears Wire Report</span>
          <span>Analysis scope: {gameMetadata.analysisScope}</span>
        </div>
      </footer>
    </div>
  );
};

export default BrownWireReport;
